/**
 * Environment configuration with safe defaults
 * All URLs and sensitive config should be pulled from here
 */

// Application URLs
export const APP_BASE_URL = process.env.REACT_APP_BASE_URL || 'songaday-net-dev.firebaseapp.com';
export const SONG_SHARE_URL = process.env.REACT_APP_SONG_SHARE_URL || 'songaday-net-dev.firebaseapp.com';

// App Configuration
export const APP_TITLE = process.env.REACT_APP_TITLE || 'Song Share';
export const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'hi@schoolofsong.org';

// Single source of truth for "is this a SADFAM instance" — several pages
// (Mission, Menu) have SADFAM-specific copy/links that shouldn't ship to
// every other instance. Checks both known SADFAM REACT_APP_TITLE values —
// prod (song-a-day) uses "SADFAM", dev (sadfam-dev) uses "Song A Day" — so
// there's one place to update if either ever changes.
export const IS_SADFAM = APP_TITLE === 'SADFAM' || APP_TITLE === 'Song A Day';

// Single source of truth for "is this the School of Song instance" (prod
// and dev both use the title "School of Song").
export const IS_SCHOOL_OF_SONG = APP_TITLE === 'School of Song';

// SADFAM and School of Song keep the original vibrant favorite-green
// (#69bb7b) on outline/clear buttons ("Play All") even though it fails
// WCAG contrast as button text (~2.1:1) — an accepted brand tradeoff for
// those two products specifically. Every other instance (the Song Share
// family: rc-song-share, songaday-net, songaday-net-dev) gets an
// accessible compromise shade instead. See the `vibrant-favorite-green`
// class applied in App.tsx and its CSS in theme/variables.css.
export const USE_VIBRANT_FAVORITE_GREEN = IS_SADFAM || IS_SCHOOL_OF_SONG;

// Helper for referencing icons in /public/assets/icon/. The actual icon
// files are swapped per Firebase Hosting target by scripts/stage-instance.js,
// which overlays config/instances/<name>/public over ./public at build time.
export const iconUrl = (filename: string): string =>
  `/assets/icon/${filename.replace(/^\//, '')}`;

// Feature Flags
// Gates the anonymous-viewer `where('privacy', '==', 'public')` filter on
// artist directory/region queries (DirectoryService, ArtistService). That
// filter is REQUIRED for instances running the canReadArtist() Firestore
// rule (else Firestore denies the unfiltered query wholesale) — but it's
// ACTIVELY HARMFUL on any instance that hasn't (a) deployed that rule, (b)
// backfilled `privacy` onto every artist doc, and (c) deployed the
// supporting composite indexes: an unfiltered query still works fine there
// under the old rules, while the filtered one silently returns zero rows
// (or errors outright without the index) for any anonymous visitor. Only
// flip this on for an instance after completing all three steps — see the
// 2026-08-16 incident where shipping the filtered query to every instance
// broke the public /artists page on SADFAM and Song Share prod, neither of
// which had been backfilled.
export const ARTIST_PRIVACY_ENFORCED = process.env.REACT_APP_ARTIST_PRIVACY_ENFORCED?.toLowerCase() === 'true';
// Set to 'true' for educational sites with groups/assignments, 'false' for pure social network
export const ENABLE_GROUPS = process.env.REACT_APP_ENABLE_GROUPS?.toLowerCase() !== 'false';
// Set to 'true' to enable magic link (email) login alongside Google sign-in
export const ENABLE_MAGIC_LINK = process.env.REACT_APP_ENABLE_MAGIC_LINK?.toLowerCase() === 'true';
// Set to 'true' to show the Support/Donate page in the menu
export const ENABLE_SUPPORT = process.env.REACT_APP_ENABLE_SUPPORT?.toLowerCase() === 'true';
// Set to 'false' to hide the Regions menu entry and the region selector on the
// Account page. Defaults to enabled.
export const ENABLE_REGIONS = process.env.REACT_APP_ENABLE_REGIONS?.toLowerCase() !== 'false';
// Set to 'true' to enable song revisions (re-recording a song as a tracked
// revision of an original). Opt-in: only sadfam-dev and song-a-day enable it.
export const ENABLE_REVISIONS = process.env.REACT_APP_ENABLE_REVISIONS?.toLowerCase() === 'true';
// Default privacy tier for newly transmitted songs when neither the selected
// group's `defaultPrivacy` nor the artist profile specifies one. Groups'
// defaultPrivacy wins over this; the student's per-song choice wins over both.
export const DEFAULT_SONG_PRIVACY: 'public' | 'member' =
  process.env.REACT_APP_DEFAULT_SONG_PRIVACY?.toLowerCase() === 'member' ? 'member' : 'public';
// SON-31: when 'true', the side-menu feed entry is labelled "Songs" and sits
// just above "Artists" (the songaday-net / Song Share instance). Default keeps
// the legacy "Song Feed" label below "Artists" for every other instance.
export const FEED_AS_SONGS = process.env.REACT_APP_FEED_AS_SONGS?.toLowerCase() === 'true';

// Search Indexes (Algolia)
// Defaults match the repo-default songaday-net instance. Other instances set
// REACT_APP_ARTIST_INDEX / REACT_APP_SONG_INDEX in their config/instances/<n>/env.
export const ARTIST_INDEX = process.env.REACT_APP_ARTIST_INDEX || 'artist-song-share';
export const SONG_INDEX = process.env.REACT_APP_SONG_INDEX || 'song-share';

// Firebase Configuration
// Reads from REACT_APP_FIREBASE_* env vars set per deployment target.
// Defaults to songaday-net-dev for safety if no .env target is loaded.
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyDTmm_e8foz5G8TwXqivlqhg5e0KNiqv0g',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'songaday-net-dev.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'songaday-net-dev',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'songaday-net-dev.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '896039456820',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:896039456820:web:b34cc2528839367df5fa61',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// No AWS config: image uploads go to Firebase Storage (TransmissionService.UploadImage).
// Never add AWS keys or S3 upload policies here — every REACT_APP_* value ships
// in the public bundle (UMich scan finding "Third-Party Service Secret Disclosure").

// Helper to get the base URL without trailing slash
export const getBaseUrl = (): string => APP_BASE_URL.replace(/\/$/, '');

// Helper to construct full URLs
export const buildUrl = (path: string): string => {
  const base = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
};
